// LegacyMount — the safety net (BUILD-GUIDE rule 5). Renders an old page at
// /profile/legacy/<x> exactly as it is today, even though that page's code
// hard-codes its own paths (ResearchLayout's nested <Switch> matches
// "/profile/research/…", its links push "/profile/research/…").
//
// It does this with a prefixed history: inside the mount, the router sees
// `/profile/legacy/research/edge` as `/profile/research/edge`, and any push /
// replace / <Link> to `/profile/research…` is sent to
// `/profile/legacy/research…`. Paths outside the prefix (e.g. a link to
// /profile/traderform) pass through untouched and leave the legacy area.
import React, { useMemo } from 'react';
import { Router, useHistory } from 'react-router-dom';

const under = (path, prefix) => typeof path === 'string' && (path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}?`) || path.startsWith(`${prefix}#`));

const swap = (path, from, to) => (under(path, from) ? to + path.slice(from.length) : path);

/** Map a location (object) or path (string) between the outer and inner views. */
function mapTo(to, from, dest) {
  if (typeof to === 'string') return swap(to, from, dest);
  if (to && typeof to === 'object') return { ...to, pathname: swap(to.pathname, from, dest) };
  return to;
}

/**
 * createPrefixedHistory(base, inner, outer)
 *   inner: the path the page thinks it lives at ('/profile/research')
 *   outer: the real URL prefix ('/profile/legacy/research')
 */
export function createPrefixedHistory(base, inner, outer) {
  const toOuter = (loc) => mapTo(loc, inner, outer);
  const toInner = (loc) => mapTo(loc, outer, inner);
  return {
    get length() { return base.length; },
    get action() { return base.action; },
    get location() { return toInner(base.location); },
    push: (path, state) => base.push(toOuter(path), state),
    replace: (path, state) => base.replace(toOuter(path), state),
    go: (n) => base.go(n),
    goBack: () => base.goBack(),
    goForward: () => base.goForward(),
    block: (prompt) => base.block(prompt),
    listen: (fn) => base.listen((loc, action) => fn(toInner(loc), action)),
    createHref: (loc) => base.createHref(toOuter(loc)),
  };
}

export default function LegacyMount({ inner, outer, children }) {
  const base = useHistory();
  const history = useMemo(() => createPrefixedHistory(base, inner, outer), [base, inner, outer]);
  return <Router history={history}>{children}</Router>;
}
